/**
 * MapMark markup design system.
 *
 * Every size is derived from one unit `u` = (longest image side / 1000) × scale,
 * so a document renders identically at any zoom and in the exported image, and
 * a 2,000 px screenshot printed ~7 in wide yields ≈ 9 pt labels and 0.25 mm
 * leaders — the drafting-standard minimums (USACE A/E/C, ASME Y14.2).
 *
 * Colours are an Okabe–Ito based, colour-vision-safe, grayscale-monotonic set.
 */
import { AREA_COLOR_KEYS, ELEV_COLOR_KEYS, MATERIALS, materialByKey, type AreaColorKey, type ElevColorKey } from '../../shared/doc.ts';

export const INK = '#1A1A1A';
export const PAPER = '#FFFFFF';
export const MARKER = '#D55E00'; // vermilion: a note's red (tests are PASS green or FAIL red)
export const FAIL = '#B0201E';
export const PASS = '#007A5C';
/**
 * The work area colours: each a body (multiplied over the plan at AREA_FILL_ALPHA, so it reads as
 * a clear, light tint) and the deeper shade of it for the border, the label, the arrow and a callout
 * on it. Green first — the default — then yellow and the rest, as the strip shows them.
 */
export interface AreaColor {
  key: AreaColorKey;
  name: string;
  fill: string;
  stroke: string;
}
export const AREA_COLORS: readonly AreaColor[] = [
  { key: 'green', name: 'Green', fill: '#5FD36E', stroke: '#1B7F3B' },
  { key: 'yellow', name: 'Yellow', fill: '#F0E442', stroke: '#B38600' },
  { key: 'blue', name: 'Blue', fill: '#5FB4EC', stroke: '#0D5FA8' },
  { key: 'orange', name: 'Orange', fill: '#F6A83A', stroke: '#B35A06' },
  { key: 'purple', name: 'Purple', fill: '#B08BEB', stroke: '#6236B5' },
  { key: 'pink', name: 'Pink', fill: '#F28CC6', stroke: '#B52F7C' },
  { key: 'teal', name: 'Teal', fill: '#4FD1C5', stroke: '#0E7C78' },
  { key: 'lime', name: 'Lime', fill: '#C2E04C', stroke: '#6B8A00' },
  { key: 'brown', name: 'Brown', fill: '#CFA57C', stroke: '#7A4B1A' },
  { key: 'slate', name: 'Slate', fill: '#9FB6CC', stroke: '#3C5A78' },
  { key: 'indigo', name: 'Indigo', fill: '#8DA0F2', stroke: '#2F3FB0' },
];
/** The default work area colour (the first of the palette). */
export const AREA_DEFAULT: AreaColor = AREA_COLORS[0]!;
/**
 * A work area's colours, from what is stored on it: a palette name, a colour of the inspector's own
 * (`#rrggbb`: the body is that colour and the border a deep shade of it), or nothing (the default).
 */
export function areaColors(color: string | null | undefined): { fill: string; stroke: string } {
  if (!color) return AREA_DEFAULT;
  const named = AREA_COLORS.find((c) => c.key === color);
  if (named) return named;
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    const [h, s, l] = hexToHsl(color);
    // the border: the same hue, deeper (dark enough to read as a line on white, but never black)
    return { fill: color, stroke: hslToHex(h, Math.min(1, Math.max(0.45, s * 1.1)), Math.min(0.34, Math.max(0.22, l * 0.5))) };
  }
  return AREA_DEFAULT;
}
/** A colour name as the strip and legend say it (a custom colour is "Custom"). */
export function areaColorName(color: string | null | undefined): string {
  return (color && AREA_COLORS.find((c) => c.key === color)?.name) || (color && color.startsWith('#') ? 'Custom' : AREA_DEFAULT.name);
}
export { AREA_COLOR_KEYS };
/** @deprecated the default area colours; marks carry their own (see `areaColors`). */
export const AREA_FILL = AREA_DEFAULT.fill;
export const AREA_STROKE = AREA_DEFAULT.stroke;
export const AREA_FILL_ALPHA = 0.45;
/**
 * The elevation tags' colours, each solid behind white type (at least 4.5 : 1 against it): a bright,
 * forward blue unless the inspector chooses otherwise (2026-10-23, Ricky: blue after all, but not the
 * dark, dull one) — fully saturated, and still 5.2 : 1 behind white.
 */
export const ELEV_COLORS: readonly { key: ElevColorKey; name: string; color: string }[] = [
  { key: 'blue', name: 'Blue', color: '#005EFF' },
  { key: 'red', name: 'Red', color: '#DA2C24' },
  { key: 'ink', name: 'Black', color: '#22262E' },
  { key: 'purple', name: 'Purple', color: '#6236B5' },
  { key: 'orange', name: 'Orange', color: '#B4500A' },
  { key: 'teal', name: 'Teal', color: '#0B6E6A' },
];
/** An elevation tag's colour from the map's choice: a palette name, a colour of one's own (`#rrggbb`), or nothing (the bright blue). */
export function elevColor(key: string | null | undefined): string {
  if (key && /^#[0-9a-f]{6}$/i.test(key)) return key.toLowerCase();
  return (ELEV_COLORS.find((c) => c.key === key) ?? ELEV_COLORS[0]!).color;
}
/** A tag colour's name, as the settings say it (a colour of one's own is "Custom"). */
export function elevColorName(key: string | null | undefined): string {
  return key?.startsWith('#') ? 'Custom' : (ELEV_COLORS.find((c) => c.key === key) ?? ELEV_COLORS[0]!).name;
}
/**
 * The type on a tag of a colour: white, as on the palette's colours — unless a colour of one's own is
 * so light that white would not read on it (under 3 : 1), when it is ink.
 */
export function elevInk(color: string): string {
  const n = parseInt(color.slice(1), 16);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return 1.05 / (l + 0.05) >= 3 ? PAPER : INK;
}
export { ELEV_COLOR_KEYS };
export const FAIL_AREA_FILL = '#E0453D'; // a failing area's body (its border and label are FAIL)
export const BAND_ALPHA = 0.5; // translucent body of a mark (multiplied over the plan)
export const EDGE_ALPHA = 1; // its border, on the plan's own line (at full strength: it has to read on any plan)
export const LEGEND_BORDER = '#004761';

export const FONT_FAMILY = '"MapMark Inter", "Inter", -apple-system, "Helvetica Neue", Arial, sans-serif';

/** Colour keys usable by notes. */
export const NOTE_COLORS: Record<string, string> = {
  red: MARKER,
  ink: INK,
  blue: '#0072B2',
  green: PASS,
  amber: AREA_STROKE,
};
export function noteColor(key: string): string {
  return NOTE_COLORS[key] ?? materialByKey(key).color;
}
export function materialColor(key: string | null): string {
  return materialByKey(key).color;
}
/** A very dark shade of a colour (almost black, still its hue): callout text over a border and arrow of the colour itself. */
export function darkOf(hex: string): string {
  const [h, s] = hexToHsl(hex);
  return hslToHex(h, Math.min(0.75, s * 0.9), 0.2);
}
/** A colour part of the way (t: 0 … 1) to another. */
export function mixHex(a: string, b: string, t: number): string {
  const x = parseInt(a.slice(1), 16),
    y = parseInt(b.slice(1), 16);
  const ch = (s: number) => Math.round(((x >> s) & 255) + (((y >> s) & 255) - ((x >> s) & 255)) * t);
  return `#${((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1)}`;
}
/** The colour as an rgba() string at the given alpha (tints behind fields). */
export function tintOf(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
function hexToHsl(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255,
    g = ((n >> 8) & 255) / 255,
    b = (n & 255) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h / 6, s, l];
}
function hslToHex(h: number, s: number, l: number): string {
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
export { MATERIALS };

export interface Metrics {
  /** Which kind of map: a soils map's labels have a scale of their own (one size of type, one box height, one corner, one line weight); a concrete map's are as they always were. */
  kind: 'soils' | 'concrete';
  u: number;
  font: number; // test/area/note label font size (on a soils map: every word, a test's badge's too)
  fontCallout: number;
  fontLegend: number;
  lineHeight: number; // multiplier
  /** A pinpoint as drawn — a test's dot, a shot's ring: its radius, the same for every one on every map. */
  markerR: number;
  /** The white ring beyond a pinpoint, parting it from the plan. */
  markerHalo: number;
  leader: number; // leader line width
  leaderHalo: number; // white casing each side
  landing: number; // horizontal landing length
  labelStub: number; // the straight stub out of the centre of a soils label's edge, before its leader turns to the test
  /** A one-line box's height as a multiple of its type's size: badges, tags, labels and callouts alike. */
  boxRatio: number;
  /** A box's padding beside its words, as a multiple of its type's size. */
  padRatio: number;
  badgeBorder: number;
  pillPadX: number;
  pillPadY: number;
  pillRadius: number;
  pillBorder: number;
  calloutBorder: number; // callout / note box border (one weight all round)
  calloutRadius: number;
  calloutGap: number; // a callout box keeps at least this far from its mark: the arrowhead always shows
  leaderStub: number; // the straight drop from the callout box before the leader angles off to the mark
  tipGap: number; // the arrow's tip stops this far short of the mark: close, never touching
  accentBar: number;
  areaBorder: number;
  /** The white halo beyond every mark's colour band, parting it from the plan round it. */
  markHalo: number;
  areaRadius: number;
  cornerRadius: number; // rounded corners of drawn band/area polygons
  bandEdge: number;
  bandDefault: number; // default highlight band width
  arrowLen: number;
  arrowWidth: number;
  gap: number; // clearance between leader tip and marker
  legendPad: number;
  legendSwatch: number;
  /** The legend's line pitch, and its box's corner. */
  legendRow: number;
  legendRadius: number;
}

/**
 * The map's sizes, all from its unit `u` (its long side / 1000 × its markup scale).
 *
 * A soils map's markup is one family, read in a report at a quarter of a page: two sizes of type —
 * every word at 18u (1.8 % of the plan's long side), a test's badge's too (2026-10-24, Ricky: its
 * number the size of every other word) — every one-line box 1.6 times its type high with 0.45 of
 * it beside the words, every box cornered at 6u, every coloured line (leaders, box borders, a
 * shot's ring) 2u, every pinpoint 3u across its radius (a shot's white centre the line's width, its
 * ring the line's weight, a test's dot as large), and every one of them parted from the plan by
 * 1.2u of white. A concrete map keeps its own sizes; only its leaders, their white casing and the
 * pinpoints are the same.
 */
export function metrics(w: number, h: number, scale = 1, kind: 'soils' | 'concrete' = 'concrete'): Metrics {
  const u = (Math.max(w, h) / 1000) * scale;
  const soils = kind === 'soils';
  const font = 18 * u;
  const fontCallout = soils ? font : 16 * u;
  const leaderHalo = 1.2 * u;
  const arrowLen = soils ? 12 * u : 0.8 * fontCallout;
  const tipGap = 2.0 * u + leaderHalo + 0.5 * u; // beyond the colour band (bandEdge) by the head's white casing (leaderHalo) and a hair: neither digs in
  const boxRatio = 1.6;
  const lineHeight = 1.25;
  return {
    kind,
    u,
    font,
    fontCallout,
    fontLegend: soils ? font : 13 * u,
    lineHeight,
    // a pinpoint: a shot's white centre as wide as a line, its ring a line's weight round it; a test's dot as large
    markerR: 3 * u,
    markerHalo: leaderHalo,
    leader: 2 * u, // every leader, on either kind of map (a soils box border is the same)
    leaderHalo, // (the tip's clearance, tipGap above, allows for it)
    landing: font * 0.9,
    labelStub: font * 0.6,
    boxRatio,
    padRatio: 0.45,
    badgeBorder: soils ? 2 * u : 1.3 * u,
    pillPadX: 0.45 * font,
    // (a soils box is 1.6 times its type high: its line, and the rest above and below it)
    pillPadY: soils ? ((boxRatio - lineHeight) / 2) * font : 0.24 * font,
    pillRadius: soils ? 6 * u : 0.32 * font,
    pillBorder: soils ? 2 * u : 1.0 * u,
    calloutBorder: soils ? 2 * u : 2.4 * u,
    calloutRadius: soils ? 6 * u : 2.2 * u,
    calloutGap: tipGap + 1.5 * arrowLen + u, // the box keeps the head and a line behind it as long again as half the head clear (HEAD_RUN, callout.ts)
    leaderStub: 0.6 * fontCallout,
    tipGap,
    accentBar: 2.6 * u,
    areaBorder: 2.2 * u,
    markHalo: 1.1 * u,
    areaRadius: 6 * u,
    cornerRadius: 1.2 * u,
    bandEdge: 2.0 * u, // the colour band outside the plan's line
    bandDefault: 22 * u,
    arrowLen,
    arrowWidth: soils ? 9 * u : 0.7 * arrowLen,
    gap: 1.5 * u,
    legendPad: 0.6 * font,
    legendSwatch: 0.9 * font,
    legendRow: soils ? boxRatio * font : 1.5 * 13 * u,
    legendRadius: soils ? 6 * u : 3 * u,
  };
}

export function fontString(weight: number, size: number, italic = false): string {
  return `${italic ? 'italic ' : ''}${weight} ${size}px ${FONT_FAMILY}`;
}
